import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, FileUp, X } from "lucide-react";
import client from "@/lib/api";

export const KEYWORD_CREDIT_COST = 1;
export const MAX_BATCH = 50;
const ALLOWED_INTENTS = new Set(["informational", "commercial", "transactional", "navigational"]);

// Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes ("").
// Returns an array of string arrays. Kept intentionally dependency-free.
export function parseCSV(text) {
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (inQuotes) {
            if (c === '"') {
                if (text[i + 1] === '"') { field += '"'; i++; }
                else { inQuotes = false; }
            } else { field += c; }
        } else {
            if (c === '"') { inQuotes = true; }
            else if (c === ",") { row.push(field); field = ""; }
            else if (c === "\n" || c === "\r") {
                if (c === "\r" && text[i + 1] === "\n") i++;
                row.push(field); field = "";
                if (row.some((v) => v.trim() !== "")) rows.push(row);
                row = [];
            } else { field += c; }
        }
    }
    if (field !== "" || row.length) {
        row.push(field);
        if (row.some((v) => v.trim() !== "")) rows.push(row);
    }
    return rows;
}

// Map parsed rows to {keyword, location, intent}. First row may be a header;
// detect by looking for the word "keyword"/"kw"/"term". Otherwise cols 0/1/2.
export function csvToBatchItems(text) {
    const rows = parseCSV(text);
    if (!rows.length) return [];
    const first = rows[0].map((c) => (c || "").trim().toLowerCase());
    const hasHeader = first.some((c) => c === "keyword" || c === "kw" || c === "term");
    let idxKw = 0, idxLoc = 1, idxIntent = 2;
    let dataRows = rows;
    if (hasHeader) {
        idxKw = first.findIndex((c) => c === "keyword" || c === "kw" || c === "term");
        const locIdx = first.findIndex((c) => c === "location" || c === "country" || c === "region");
        const intIdx = first.findIndex((c) => c === "intent" || c === "search_intent");
        if (locIdx >= 0) idxLoc = locIdx;
        if (intIdx >= 0) idxIntent = intIdx;
        dataRows = rows.slice(1);
    }
    return dataRows.map((r) => ({
        keyword: (r[idxKw] || "").trim(),
        location: (r[idxLoc] || "").trim() || null,
        intent: ((r[idxIntent] || "").trim().toLowerCase()) || null,
    })).filter((it) => it.keyword);
}

// Textarea → normalized items (paste mode). Empty entries and dupes dropped.
function parseTextarea(raw) {
    const seen = new Set();
    const out = [];
    for (const line of (raw || "").split(/[\n,]+/)) {
        const k = line.trim().toLowerCase();
        if (!k || seen.has(k)) continue;
        seen.add(k);
        out.push({ keyword: k, location: null, intent: null });
        if (out.length >= MAX_BATCH) break;
    }
    return out;
}

// Merge, dedupe (case-insensitive), sanitize intent, cap at MAX_BATCH.
function normalizeItems(items) {
    const seen = new Set();
    const out = [];
    for (const it of items) {
        const k = (it.keyword || "").trim().toLowerCase();
        if (!k || seen.has(k)) continue;
        seen.add(k);
        const intent = (it.intent || "").toLowerCase();
        out.push({
            keyword: k,
            location: (it.location || "").trim() || null,
            intent: ALLOWED_INTENTS.has(intent) ? intent : null,
        });
        if (out.length >= MAX_BATCH) break;
    }
    return out;
}

/**
 * <BatchImportModal />
 * Renders the two-tab (Paste / CSV) batch keyword import overlay.
 *
 * Props:
 *   open              — boolean, controls visibility
 *   onClose()         — invoked when user closes/cancels
 *   projectId         — string, target project
 *   existingKeywords  — array of already-tracked keyword strings (for dupe detection)
 *   limit             — plan cap for total keywords on this project
 *   tier              — user's tier label (shown in "over limit" message)
 *   onSuccess(added)  — called with the array of newly-inserted keyword docs
 */
export function BatchImportModal({
    open, onClose, projectId, existingKeywords, limit, tier, onSuccess,
}) {
    const [batchText, setBatchText] = useState("");
    const [batchItems, setBatchItems] = useState([]);
    const [batchMode, setBatchMode] = useState("paste"); // "paste" | "csv"
    const [batchFileName, setBatchFileName] = useState("");
    const [batchSubmitting, setBatchSubmitting] = useState(false);
    const [creditBalance, setCreditBalance] = useState(null);

    // Reset all state whenever the modal opens fresh
    useEffect(() => {
        if (!open) return;
        setBatchText("");
        setBatchItems([]);
        setBatchFileName("");
        setBatchMode("paste");
        client.get("/usage/me").then((r) => {
            setCreditBalance({
                total_available: r.data?.credits?.total_available ?? 0,
                monthly_remaining: r.data?.credits?.monthly_remaining ?? 0,
                topup_balance: r.data?.credits?.topup_balance ?? 0,
                is_admin: !!r.data?.credits?.is_admin,
            });
        }).catch(() => setCreditBalance(null));
    }, [open]);

    // Derived: current parsed batch (paste OR csv)
    const parsed = useMemo(() => (
        batchMode === "csv" ? normalizeItems(batchItems) : parseTextarea(batchText)
    ), [batchMode, batchText, batchItems]);

    const existingSet = useMemo(
        () => new Set((existingKeywords || []).map((k) => k.toLowerCase())),
        [existingKeywords],
    );
    const newlyAdded = parsed.filter((it) => !existingSet.has(it.keyword));
    const duplicatesInBatch = parsed.length - newlyAdded.length;
    const rowCount = (existingKeywords || []).length;
    const room = Math.max(0, limit - rowCount);
    const willInsert = Math.min(newlyAdded.length, room);
    const willSkipOverLimit = newlyAdded.length - willInsert;
    const totalCost = willInsert * KEYWORD_CREDIT_COST;

    const availableCredits = creditBalance?.total_available ?? null;
    const isAdmin = !!creditBalance?.is_admin;
    const insufficientCredits = !isAdmin && availableCredits !== null && totalCost > availableCredits;

    const handleCsvFile = (file) => {
        if (!file) return;
        if (file.size > 512 * 1024) {
            toast.error("CSV too large (max 512 KB).");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const items = csvToBatchItems(String(reader.result || ""));
                if (!items.length) {
                    toast.error("No keywords found in the CSV.");
                    return;
                }
                setBatchItems(items);
                setBatchFileName(file.name);
                setBatchMode("csv");
                toast.success(`Loaded ${items.length} row${items.length === 1 ? "" : "s"} from ${file.name}`);
            } catch {
                toast.error("Could not parse CSV.");
            }
        };
        reader.onerror = () => toast.error("Failed to read file.");
        reader.readAsText(file);
    };

    const submit = async () => {
        if (!parsed.length) {
            toast.error(batchMode === "csv" ? "Upload a CSV with at least one row." : "Paste at least one keyword.");
            return;
        }
        if (willInsert === 0) {
            toast.error(duplicatesInBatch === parsed.length
                ? "All of these are already tracked."
                : "Plan limit reached — upgrade to add more.");
            return;
        }
        if (insufficientCredits) {
            toast.error(`Not enough credits: need ${totalCost}, have ${availableCredits}.`);
            return;
        }
        setBatchSubmitting(true);
        try {
            const r = await client.post(`/projects/${projectId}/keywords/batch`, { keywords: parsed });
            const { added, skipped_duplicates, skipped_over_limit, keywords } = r.data;
            onSuccess?.(keywords);
            const parts = [`Added ${added} keyword${added === 1 ? "" : "s"}`];
            if (skipped_duplicates) parts.push(`skipped ${skipped_duplicates} duplicate${skipped_duplicates === 1 ? "" : "s"}`);
            if (skipped_over_limit) parts.push(`${skipped_over_limit} over plan limit`);
            toast.success(parts.join(" · "));
            onClose?.();
        } catch (err) {
            if (err?.response?.status === 402) {
                toast.error(err.response.data.detail || "Out of credits or plan limit reached.");
            } else {
                toast.error(err?.response?.data?.detail || "Batch import failed.");
            }
        } finally {
            setBatchSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="batch-import-overlay"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                data-testid="batch-import-modal"
                className="bg-card border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-5 border-b border-border">
                    <div>
                        <div className="overline">Batch import</div>
                        <h2 className="font-display text-2xl tracking-tight mt-1">Add up to {MAX_BATCH} keywords</h2>
                    </div>
                    <button
                        data-testid="batch-import-close"
                        onClick={onClose}
                        className="text-muted-foreground hover:text-foreground p-1"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 space-y-4">
                    {/* Mode tabs — Paste vs CSV */}
                    <div className="flex items-center gap-1 border border-border w-fit p-0.5" role="tablist">
                        <button
                            data-testid="batch-mode-paste"
                            role="tab"
                            aria-selected={batchMode === "paste"}
                            onClick={() => setBatchMode("paste")}
                            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${batchMode === "paste" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            Paste
                        </button>
                        <button
                            data-testid="batch-mode-csv"
                            role="tab"
                            aria-selected={batchMode === "csv"}
                            onClick={() => setBatchMode("csv")}
                            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${batchMode === "csv" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                        >
                            CSV upload
                        </button>
                    </div>

                    {batchMode === "paste" ? (
                        <>
                            <p className="text-sm text-muted-foreground">
                                Paste keywords, one per line (or comma-separated). Duplicates and empty lines are ignored.
                            </p>
                            <textarea
                                data-testid="batch-import-textarea"
                                value={batchText}
                                onChange={(e) => setBatchText(e.target.value)}
                                placeholder={"seo tools\nkeyword research\nbacklink checker\n…"}
                                rows={10}
                                className="w-full bg-input border border-border px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary resize-y"
                            />
                        </>
                    ) : (
                        <>
                            <p className="text-sm text-muted-foreground">
                                Drop a CSV with columns: <span className="font-mono text-foreground">keyword</span>, <span className="font-mono text-foreground">location</span> (optional), <span className="font-mono text-foreground">intent</span> (optional).
                                Header row is auto-detected.
                            </p>
                            <label
                                htmlFor="batch-csv-file"
                                data-testid="batch-csv-dropzone"
                                onDragOver={(e) => { e.preventDefault(); }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    const f = e.dataTransfer?.files?.[0];
                                    if (f) handleCsvFile(f);
                                }}
                                className="block border border-dashed border-border hover:border-primary bg-input/50 p-6 cursor-pointer text-center transition"
                            >
                                <FileUp className="w-6 h-6 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
                                <div className="text-sm font-mono text-foreground">
                                    {batchFileName ? batchFileName : "Drop CSV here or click to browse"}
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">Max 512 KB · {MAX_BATCH} rows</div>
                                <input
                                    id="batch-csv-file"
                                    data-testid="batch-csv-file-input"
                                    type="file"
                                    accept=".csv,text/csv"
                                    onChange={(e) => handleCsvFile(e.target.files?.[0])}
                                    className="hidden"
                                />
                            </label>

                            {batchItems.length > 0 && (
                                <div data-testid="batch-csv-preview" className="border border-border max-h-52 overflow-y-auto">
                                    <table className="w-full text-xs font-mono">
                                        <thead className="text-muted-foreground">
                                            <tr className="border-b border-border">
                                                <th className="text-left p-2">Keyword</th>
                                                <th className="text-left p-2">Location</th>
                                                <th className="text-left p-2">Intent</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsed.slice(0, 10).map((it, i) => (
                                                <tr key={i} className="border-b border-border/50">
                                                    <td className="p-2 text-foreground">{it.keyword}</td>
                                                    <td className="p-2 text-muted-foreground">{it.location || "—"}</td>
                                                    <td className="p-2 text-muted-foreground">{it.intent || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {parsed.length > 10 && (
                                        <div className="text-[10px] text-muted-foreground p-2 text-center border-t border-border/50">
                                            +{parsed.length - 10} more row{parsed.length - 10 === 1 ? "" : "s"}
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
                        <div className="border border-border p-3">
                            <div className="text-muted-foreground">Parsed</div>
                            <div data-testid="batch-parsed-count" className="text-lg text-foreground mt-1">{parsed.length}</div>
                        </div>
                        <div className="border border-border p-3">
                            <div className="text-muted-foreground">Duplicates</div>
                            <div data-testid="batch-duplicate-count" className="text-lg text-muted-foreground mt-1">{duplicatesInBatch}</div>
                        </div>
                        <div className="border border-border p-3">
                            <div className="text-muted-foreground">Over limit</div>
                            <div data-testid="batch-over-limit-count" className={`text-lg mt-1 ${willSkipOverLimit ? "text-warning" : "text-muted-foreground"}`}>{willSkipOverLimit}</div>
                        </div>
                        <div className={`border p-3 ${insufficientCredits ? "border-destructive/60 bg-destructive/10" : "border-primary/60 bg-primary/5"}`}>
                            <div className={insufficientCredits ? "text-destructive" : "text-primary/80"}>Will add · cost</div>
                            <div data-testid="batch-cost-preview" className={`text-lg mt-1 ${insufficientCredits ? "text-destructive" : "text-primary"}`}>
                                {willInsert} · {totalCost} cr
                            </div>
                        </div>
                    </div>

                    {/* Preflight credit banner */}
                    {creditBalance !== null && (
                        insufficientCredits ? (
                            <div
                                data-testid="batch-credit-warning"
                                className="border border-destructive/60 bg-destructive/10 p-3 flex items-start gap-3"
                            >
                                <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                                <div className="flex-1 text-xs">
                                    <div className="text-destructive font-mono">
                                        Not enough credits — need {totalCost}, you have {availableCredits}.
                                    </div>
                                    <div className="text-muted-foreground mt-1">
                                        Trim the list to {availableCredits} keyword{availableCredits === 1 ? "" : "s"} or
                                        <Link to="/pricing" data-testid="batch-credit-buy-link" className="ml-1 text-primary underline">buy a credit pack →</Link>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div
                                data-testid="batch-credit-status"
                                className="text-[11px] font-mono text-muted-foreground flex items-center gap-4"
                            >
                                <span>
                                    Balance: <span className="text-foreground">{isAdmin ? "∞ (admin)" : availableCredits}</span>
                                </span>
                                {!isAdmin && (
                                    <span>
                                        After this batch: <span className="text-foreground">{Math.max(0, availableCredits - totalCost)}</span>
                                    </span>
                                )}
                            </div>
                        )
                    )}

                    {willSkipOverLimit > 0 && (
                        <div className="text-xs text-warning font-mono">
                            {willSkipOverLimit} keyword{willSkipOverLimit === 1 ? "" : "s"} will be dropped — you're at {rowCount}/{limit} on {tier}.
                            <Link to="/pricing" className="ml-1 underline">Upgrade →</Link>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
                    <button
                        data-testid="batch-import-cancel"
                        onClick={onClose}
                        className="border border-border px-4 py-2 text-sm hover:border-muted-foreground"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="batch-import-submit"
                        onClick={submit}
                        disabled={batchSubmitting || willInsert === 0 || insufficientCredits}
                        className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
                    >
                        {batchSubmitting ? "Adding…" : `Add ${willInsert} · ${totalCost} credits`}
                    </button>
                </div>
            </div>
        </div>
    );
}
